import { ADD_ROLE_FAILURE, ADD_ROLE_REQUEST, ADD_ROLE_SUCCESS } from 'actions/role/addRole.types';
import { EDIT_ROLE_FAILURE, EDIT_ROLE_REQUEST, EDIT_ROLE_SUCCESS } from 'actions/role/editRole.types';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import asObject from 'utils/objects/asObject.util';

import {
  CLEAR_ERROR,
  CLEAR_ROLE_STATE,
  DELETE_ROLE_FAILURE,
  DELETE_ROLE_REQUEST,
  DELETE_ROLE_SUCCESS,
  GET_ROLES_FAILURE,
  GET_ROLES_REQUEST,
  GET_ROLES_SUCCESS,
  REMOVE_SAVED_ROLES_IDS,
} from 'onboarding/roles/actions';

const DEFAULT = Object.freeze({
  roles: [],
  isPosting: false,
  isFetching: false,
  isSaved: false,
  settingsSavedRolesIDs: [],
  settingsErrorRolesIDs: [],
  deleteMessage: '',
});

const reduceAddSuccess = (state, { response }) => {
  const role = response;
  const roles = state.roles.slice();
  if (!roles.find((value) => value.id && value.id === role.id)) {
    roles.push(role);
  }
  return { ...state, roles, isPosting: false, isSaved: true };
};

const reduceEditSuccess = (state, { response }) => {
  const roles = state.roles.slice();
  for (let i = 0; i < roles.length; i += 1) {
    if (roles[i].id === response.id) {
      roles[i] = response;
      break;
    }
  }

  const settingsSavedRolesIDs = state.settingsSavedRolesIDs.slice();
  const responseId = response.id;

  const settingsErrorRolesIDs = state.settingsErrorRolesIDs.slice().filter((cat) => cat.id !== responseId);

  const savedCatInd = settingsSavedRolesIDs.findIndex((cat) => cat.id === responseId);
  if (response.id && savedCatInd === -1) {
    settingsSavedRolesIDs.push({ id: response.id, isNew: true });
  } else {
    settingsSavedRolesIDs[savedCatInd].isNew = true;
  }

  return { ...state, roles, settingsSavedRolesIDs, settingsErrorRolesIDs, isPosting: false, isSaved: true };
};

const reduceEditFailure = (state, { params, errorMessage }) => {
  const settingsErrorRolesIDs = state.settingsErrorRolesIDs.slice();
  const responseId = params.roleId;
  const savedCatInd = settingsErrorRolesIDs.findIndex((cat) => cat.id === responseId);

  if (savedCatInd === -1) {
    settingsErrorRolesIDs.push({ id: responseId, message: errorMessage });
  } else {
    settingsErrorRolesIDs[savedCatInd].message = errorMessage;
  }

  return { ...state, settingsErrorRolesIDs, errorMessage, isPosting: false };
};

const reduceDelSuccess = (state, { response, params }) => {
  // response is {} when no response
  const responseKeysLength = Object.keys(response).length;
  const roles = [...state.roles];

  if (!responseKeysLength) {
    for (let i = 0; i < roles.length; i += 1) {
      if (roles[i].id === params.id) {
        roles.splice(i, 1);
        break;
      }
    }
  }
  return { ...state, roles, isPosting: false, deleteMessage: responseKeysLength ? response : '' };
};

const reduceClearRoleState = (state) => ({
  ...state,
  isPosting: false,
  isFetching: false,
  isSaved: false,
  errorMessage: '',
});

const reduceRemoveSavedIds = (state, { ids }) => {
  const settingsSavedRolesIDs = state.settingsSavedRolesIDs.filter((category) => ids.indexOf(category.id) < 0);
  return {
    ...state,
    settingsSavedRolesIDs,
  };
};

const reduceAddRequest = (state) => ({ ...state, isPosting: true });
const reduceAddFailure = (state, { errorMessage }) => ({ ...state, isPosting: false, errorMessage });

const reduceGetRequest = (state) => ({ ...state, isFetching: true });
const reduceGetSuccess = (state, { response }) => ({ ...state, isFetching: false, roles: response });
const reduceGetFailure = (state, { errorMessage }) => ({ ...state, isFetching: false, errorMessage });

const reduceEditRequest = (state) => ({ ...state, isPosting: true });
const reduceDelRequest = (state) => ({ ...state, isPosting: true });

const reduceDelFailure = (state, { errorMessage }) => ({ ...state, isPosting: false, errorMessage });
const reduceClearError = (state) => ({ ...state, deleteMessage: '' });

const MAP = Object.freeze({
  [ADD_ROLE_REQUEST]: reduceAddRequest,
  [ADD_ROLE_SUCCESS]: reduceAddSuccess,
  [ADD_ROLE_FAILURE]: reduceAddFailure,
  [GET_ROLES_REQUEST]: reduceGetRequest,
  [GET_ROLES_SUCCESS]: reduceGetSuccess,
  [GET_ROLES_FAILURE]: reduceGetFailure,
  [EDIT_ROLE_REQUEST]: reduceEditRequest,
  [EDIT_ROLE_SUCCESS]: reduceEditSuccess,
  [EDIT_ROLE_FAILURE]: reduceEditFailure,
  [DELETE_ROLE_REQUEST]: reduceDelRequest,
  [DELETE_ROLE_SUCCESS]: reduceDelSuccess,
  [DELETE_ROLE_FAILURE]: reduceDelFailure,
  [CLEAR_ERROR]: reduceClearError,
  [CLEAR_ROLE_STATE]: reduceClearRoleState,
  [REMOVE_SAVED_ROLES_IDS]: reduceRemoveSavedIds,
});

const rolesReducer = (state, action) => (MAP[action && action.type] || alwaysSame)(state || { ...DEFAULT }, asObject(action));

export default rolesReducer;
